













































































import { PropType, computed, defineComponent, ref, toRefs, nextTick, onMounted, watch } from '@vue/composition-api';
import {
  AdgroupConfig,
  AdgroupFormData,
  CampaignFormData,
  AdgroupListItem,
  BudgetMode,
  CreateMode,
  EstimatedType,
  EstimateStatus,
  AudienceEstimate,
} from '@tt4b/ttam-enums';
import TtamBudget from './index';
import { computedDiffDays } from '@tt4b/ttam-utils';
import {
  useAccountCurrency,
  useStdAccountStore,
  useCurrencyFormatValidator,
  useStore,
  useAdgroupBudgetValidate,
  StateEnum,
  useInjectAbTest,
} from '@tt4b/ttam-hooks';
import { BudgetSourceType } from './types';
import { isNumber } from 'lodash-es';

const name = 'ttam-budget-compose';

export default defineComponent({
  name,
  components: {
    TtamBudgetSet: TtamBudget.TtamBudgetSet,
    TtamBudgetLearningPhaseTips: TtamBudget.TtamBudgetLearningPhaseTips,
    TtamBudgetCboInfo: TtamBudget.TtamBudgetCboInfo,
    TtamBudgetRecommend: TtamBudget.TtamBudgetRecommend,
    TtamBudgetSplittestLabelHint: TtamBudget.TtamBudgetSplittestLabelHint,
    TtamBudgetLabel: TtamBudget.TtamBudgetLabel,
    TtamBudgetSplittestLabel: TtamBudget.TtamBudgetSplittestLabel,
    TtamBudgetScheduledBudget: TtamBudget.TtamBudgetScheduledBudget,
    TtamDynamicDailyBudgetTip: TtamBudget.TtamDynamicDailyBudgetTip,
    TtamBudgetCoupon: TtamBudget.TtamBudgetCoupon,
  },
  props: {
    campaignData: {
      type: Object as PropType<CampaignFormData>,
      required: true,
      default: () => ({}),
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
      required: true,
    },
    adgroupConfig: {
      type: Object as PropType<AdgroupConfig>,
      default: () => ({}),
      required: true,
    },
    cboAdgroupList: {
      type: Array as PropType<AdgroupListItem[]>,
      default: () => [],
    },
    isLearningPhase: {
      type: Boolean,
      default: false,
    },
    createMode: {
      type: String as PropType<CreateMode>,
      default: () => CreateMode.CREATE,
    },
    responsiveStyle: {
      type: Boolean,
      default: false,
    },
    minBudgetCalculated: {
      type: Number,
    },
    minSchduledBudgetCalculated: {
      type: Number,
    },
    budgetPrefilled: {
      type: Object,
      default: () => ({
        prefilled: false,
        prefillValueInUSD: 0,
        directPrefillValue: 0,
      }),
    },
    lite: {
      type: Boolean,
      default: false,
    },
    isNewErrorMsg: {
      type: Boolean,
      default: false,
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup(props, { emit }) {
    const {
      campaignData,
      adgroupData,
      adgroupConfig,
      isLearningPhase,
      cboAdgroupList,
      createMode,
      lite,
      responsiveStyle,
      budgetPrefilled,
      minBudgetCalculated,
      isNewErrorMsg,
    } = toRefs(props);

    const { state } = useStore();
    const audienceEstimate = computed(() => state[StateEnum.audienceSize].data?.value);
    const { formatMoney } = useAccountCurrency();
    const { timezone } = useStdAccountStore();
    const _budgetSourceType = ref<BudgetSourceType | undefined>();
    const budgetRecommendNode = ref();

    const { data: budgetRecommendData, run } = TtamBudget.useRecommendDataSource({
      adgroupData,
      campaignData,
      createMode,
      audienceEstimate: computed(
        () =>
          ({
            ...(audienceEstimate?.value ?? {}),
            score: Number(audienceEstimate?.value?.estimate) || EstimatedType.NONE,
            status: audienceEstimate?.value ? EstimateStatus.SUCCESS : EstimateStatus.FAILED,
          } as AudienceEstimate),
      ),
    });

    const isCanRecommendAutoExpend = computed(() => {
      if (!adgroupData.value.budget || !budgetRecommendData.value?.competitiveBudget) {
        return false;
      }

      return Number(adgroupData.value.budget) < budgetRecommendData.value.competitiveBudget;
    });

    const { minBudget } = TtamBudget.useCommonMinBudget(minBudgetCalculated);

    const budgetRef = ref();
    const getPrefilledValue = prefilledValue => {
      if (
        prefilledValue.prefilled === true &&
        _budgetSourceType.value !== BudgetSourceType.manual &&
        !props.adgroupData.budget
      ) {
        if (prefilledValue.directPrefillValue) {
          return prefilledValue.directPrefillValue.toString();
        } else if (prefilledValue.prefillValueInUSD) {
          return (minBudget.value + TtamBudget.useCurrencyRatio(prefilledValue.prefillValueInUSD)).toString();
        }
      }
      return '';
    };
    const _budget = computed({
      get: () => {
        let formBudget = props.adgroupData.budget;
        const prefilledValue = getPrefilledValue(budgetPrefilled.value);
        if (prefilledValue) {
          formBudget = prefilledValue;
        }

        if (!formBudget) {
          return '';
        }

        return isNumber(formBudget) ? formatMoney(formBudget) : formBudget;
      },
      set: val => {
        emit('budget-change', val, _budgetSourceType.value);
        nextTick(() => {
          budgetRef.value?.validate('custom');
        });
        setTimeout(() => {
          if (isCanRecommendAutoExpend.value) {
            budgetRecommendNode?.value?.expand?.();
          }
        }, 1000);
      },
    });

    const _budgetBlur = ref(_budget.value);

    const _budgetMode = computed({
      get: () => props.adgroupData.budgetMode,
      set: v => {
        const curMode = v;
        const preMode = props.adgroupData?.budgetMode;
        if (curMode === preMode) {
          return;
        }
        emit('budget-mode-change', v);

        const { startTime, endTime } = adgroupData.value;
        const daysCount = computedDiffDays(startTime, endTime);
        let temp = Number(_budget.value);
        if (!temp) {
          return;
        }
        if (curMode === BudgetMode.TOTAL_BUDGET) {
          temp = Math.max(temp, minBudget.value) * daysCount;
        }
        if ([BudgetMode.DAILY_BUDGET, BudgetMode.DYNAMIC_DAILY_BUDGET].includes(curMode)) {
          temp = temp / daysCount;
          temp = Math.max(temp, minBudget.value);
        }
        _budget.value = formatMoney(temp);
      },
    });

    const _scheduledBudget = computed({
      get: () => props.adgroupData?.scheduledBudget ?? 0,
      set: v => emit('scheduled-budget-change', v),
    });
    const _scheduledBudgetTimestamp = computed({
      get: () => props.adgroupData.scheduledBudgetTimestamp ?? 0,
      set: v => emit('scheduled-budget-timestamp-change', v),
    });
    // TODO 待split test支持
    const isSplitTestEnabled = ref(false);

    const { budgetModeDisabled } = TtamBudget.useSetPropsBudgetModeDisabled({
      adgroupConfig,
    });

    const { budgetModeOptions } = TtamBudget.useSetPropsBudgetModeOptions({
      createMode,
      adgroupData,
      campaignData,
      lite,
    });

    const dynamicBudgetMode = computed(() => {
      const isDynamicDailyBudget = _budgetMode.value === BudgetMode.DYNAMIC_DAILY_BUDGET;
      return lite.value
        ? isDynamicDailyBudget
          ? BudgetMode.DAILY_BUDGET
          : adgroupData.value.budgetMode
        : _budgetMode.value;
    });

    const { text: cboInfoText } = TtamBudget.useCboInfoPropsText({
      adgroupData,
      campaignData,
    });

    const { currencyOption } = TtamBudget.useCommonPropsCurrencyOption();

    const { options: recommendOptions } = TtamBudget.useRecommendPropsOptions({
      budgetRecommendData,
    });

    const { visible: budgetRecommendVisible } = TtamBudget.useRecommendPropsVisible({
      adgroupData,
      campaignData,
      createMode,
      budgetRecommendData,
      minBudgetCalculated,
    });

    const { visible: scheduledBudgetVisible } = TtamBudget.useScheduledBudgetPropsVisible({
      adgroupData,
      campaignData,
      adgroupConfig,
    });

    const { visible: cboInfoVisible } = TtamBudget.useCboInfoPropsVisible({
      campaignData,
      adgroupData,
    });

    const { visible: learningPhaseTipsVisible } = TtamBudget.useLearningPhaseTipsPropsVisible({
      adgroupConfig,
      isLearningPhase,
    });

    const { placeholder: budgetSetPlaceholder } = TtamBudget.useSetPropsPlaceholder({
      adgroupData,
      minBudgetCalculated,
    });

    const onApply = (budget, budgetSourceType: BudgetSourceType) => {
      _budgetSourceType.value = budgetSourceType;
      _budget.value = budget;
    };

    const onBudgetBlur = () => {
      if (!budgetPrefilled.value?.prefilled) {
        return;
      }

      let newBudget: string = _budget.value;
      if (!newBudget || parseInt(newBudget) === 0) {
        newBudget = _budgetBlur.value;
      }

      _budget.value = newBudget;
    };

    const onBudgetChange = budget => {
      _budgetSourceType.value = BudgetSourceType.manual;
      _budget.value = budget;
    };
    const onBudgetModeChange = budget => {
      _budgetMode.value = budget;
    };

    const onBudgetChanged = budget => {
      if (budget && parseInt(budget) > 0) {
        _budgetBlur.value = formatMoney(budget);
      }
      _budget.value = formatMoney(budget);
    };

    const { budgetValidator } = useAdgroupBudgetValidate(
      {
        campaignFormData: campaignData,
        minBudgetCalculated,
        isNewErrorMsg,
      },
      emit,
    );
    const budgetSetRef = ref();
    const { rules: budgetValidatorWithCurrencyFormat } = useCurrencyFormatValidator(
      () => budgetSetRef.value?.$refs?.inputCurrency,
      () => budgetValidator.value,
    );

    onMounted(() => {
      // 自助用户，在Conversion Objective下拉取推荐预算数据
      run({
        // 非通用逻辑，暴露个hook给外面埋点
        onSuccess: () => {
          emit('get-recommend-budget-success');
        },
      });
    });

    // 新prefill逻辑预填值通过后端接口获取，会延迟获得（实验中）
    watch(
      budgetPrefilled,
      newVal => {
        const prefilledValue = getPrefilledValue(newVal);
        if (prefilledValue) {
          _budget.value = formatMoney(prefilledValue);
        }
      },
      { deep: true },
    );
    const { isCloseLoopBudgetCoupon } = useInjectAbTest();

    const budgetValidateStatus = computed(() => (budgetRef.value?.validateState === 'error' ? 'danger' : 'default'));
    return {
      name,
      budgetModeOptions,
      _budget,
      _budgetMode,
      _scheduledBudget,
      _scheduledBudgetTimestamp,

      budgetRecommendData,

      budgetModeDisabled,
      budgetRecommendVisible,
      scheduledBudgetVisible,
      learningPhaseTipsVisible,
      cboInfoVisible,
      budgetSetPlaceholder,
      cboInfoText,
      recommendOptions,
      currencyOption,
      minBudget,
      dynamicBudgetMode,

      adId: computed(() => adgroupData.value?.adId),
      timezone,
      isSplitTestEnabled,
      budgetRecommendNode,
      budgetRef,
      budgetSetRef,
      budgetValidatorWithCurrencyFormat,
      onApply,
      onBudgetBlur,
      onBudgetChange,
      onBudgetChanged,
      onBudgetModeChange,
      isCloseLoopBudgetCoupon,
      budgetValidateStatus,
    };
  },
});
